/**
 * DashboardsPage — the page shell every dashboards surface should sit in.
 *
 * It owns the three things that were being hand-rolled per page, and that a page
 * has no business deciding for itself:
 *
 *   1. **The `.nb-dashboards` scope**, which is what supplies the whole token set
 *      AND the page background (`--nb-page` — the design's mint in light, the dark
 *      cream in dark). A page that forgets the class gets the app's flat white and
 *      its cards stop reading as cards.
 *   2. **The density attribute**, read from the user's own preference. This is the
 *      reason the component exists at all: both review pages hard-pinned
 *      `data-density="comfortable"`, so a user who had chosen compact got two
 *      pages that ignored them (BON-381 U13). Reading it here makes that class of
 *      bug unavailable rather than merely fixed.
 *   3. **The page's spacing**, so two surfaces built a month apart line up.
 *
 * The HEADER is a slot rather than something this renders, because a header is
 * genuinely per-surface (crumb, title, back affordance, actions) while background
 * and spacing are not. It sits outside the padded area, full-bleed, as both
 * existing pages had it.
 *
 * Extracted from `ReviewQueuePage` and `ReviewDetailPage`, which were the same
 * markup twice with two deliberate differences — both now props.
 */
import type { ReactNode } from 'react';

import { useViewport } from '../../hooks/useViewport';

import { useDensity } from './lib/useTheme';

import './dashboards.css';

/**
 * Page padding. Tight at the top because a header always sits above it, and
 * roomier at the sides on desktop where the content is not the full viewport.
 */
export const PAGE_PADDING = {
  phone: '2px 14px 24px',
  desktop: '6px 32px 28px',
} as const;

/**
 * Phones with a FIXED bottom action bar need the page to end above it, or the
 * last card sits under the bar and cannot be reached by scrolling.
 */
export const PAGE_PADDING_WITH_BOTTOM_BAR = '2px 14px 96px' as const;

/** Gap between the stacked blocks of a page. */
export const PAGE_GAP = { phone: 16, desktop: 18 } as const;

export interface DashboardsPageProps {
  /** Full-bleed, above the padded content. Per-surface by nature. */
  header?: ReactNode;
  children: ReactNode;
  /**
   * Stack the children in a column with the standard page gap. True for a page
   * that is a list of blocks; pass false when the children own their own layout
   * (a two-column detail grid, say) and an outer gap would fight it.
   */
  stack?: boolean;
  /** Reserve room for a fixed bottom action bar on phones. */
  hasPhoneBottomBar?: boolean;
}

export function DashboardsPage({
  header,
  children,
  stack = true,
  hasPhoneBottomBar = false,
}: DashboardsPageProps) {
  const { isPhone } = useViewport();
  const { value: density } = useDensity();

  const padding = isPhone
    ? hasPhoneBottomBar
      ? PAGE_PADDING_WITH_BOTTOM_BAR
      : PAGE_PADDING.phone
    : PAGE_PADDING.desktop;

  return (
    <div
      className="nb-dashboards"
      data-density={density}
      // `min-height: 100%` is what makes the page COLOUR fill the viewport rather
      // than only the height of its content — without it a short page shows the
      // app's own background below the fold.
      style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {header}
      <div
        style={{
          flex: 1,
          padding,
          ...(stack
            ? {
                display: 'flex',
                flexDirection: 'column',
                gap: isPhone ? PAGE_GAP.phone : PAGE_GAP.desktop,
              }
            : {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}
